import { useState, useEffect, useCallback } from 'react'
import {
  fetchContentfulEntriesWithIncludes,
  resolveAssetUrl,
} from '../lib/contentful'

export interface StoredBlogPost {
  id: string
  slug: string
  title: string
  excerpt: string
  date: string
  author: string
  image: string
  category: string
  readTime: string
  body: string
  published: boolean
}

// Contentful field shape for "blogPost" content type
interface ContentfulBlogPostFields {
  title: string
  slug: string
  blogImage?: { sys: { id: string } }
  content?: string | RichTextDocument
  author?: string
  category?: string
}

interface RichTextDocument {
  nodeType: string
  content: RichTextNode[]
  data: Record<string, any>
}

interface RichTextNode {
  nodeType: string
  value?: string
  content?: RichTextNode[]
  marks?: { type: string }[]
  data?: Record<string, any>
}

function extractPlainText(
  field: string | RichTextDocument | undefined,
): string {
  if (!field) return ''
  if (typeof field === 'string') return field
  if (!field.content) return ''

  function walkNodes(nodes: RichTextNode[]): string {
    return nodes
      .map((node) => {
        if (node.nodeType === 'text' && node.value) return node.value
        if (node.content) return walkNodes(node.content)
        return ''
      })
      .join('')
  }

  return field.content
    .map((block) => {
      if (block.content) return walkNodes(block.content)
      return ''
    })
    .join('\n\n')
}

const STORAGE_KEY = 'belo_blog_posts'
const BLOG_POSTS_UPDATED_EVENT = 'belo_blog_posts_updated'

function getStoredBlogPosts(): StoredBlogPost[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return []
    return JSON.parse(raw)
  } catch {
    return []
  }
}

function saveBlogPosts(posts: StoredBlogPost[]) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(posts))
}

function dispatchBlogPostsUpdate() {
  window.dispatchEvent(new Event(BLOG_POSTS_UPDATED_EVENT))
}

function parseDate(dateStr: string): Date {
  return new Date(dateStr)
}

export function addBlogPost(data: Omit<StoredBlogPost, 'id'>): StoredBlogPost {
  const posts = getStoredBlogPosts()
  const newPost: StoredBlogPost = {
    ...data,
    id: `blog_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
  }
  posts.push(newPost)
  saveBlogPosts(posts)
  dispatchBlogPostsUpdate()
  return newPost
}

export function removeBlogPost(id: string): void {
  const posts = getStoredBlogPosts().filter((p) => p.id !== id)
  saveBlogPosts(posts)
  dispatchBlogPostsUpdate()
}

export function updateBlogPost(
  id: string,
  updates: Partial<StoredBlogPost>,
): void {
  const posts = getStoredBlogPosts()
  const index = posts.findIndex((p) => p.id === id)
  if (index !== -1) {
    posts[index] = { ...posts[index], ...updates }
    saveBlogPosts(posts)
    dispatchBlogPostsUpdate()
  }
}

function mapContentfulBlogPost(
  entry: {
    sys: { id: string; createdAt: string }
    fields: ContentfulBlogPostFields
  },
  includes?: { Asset?: any[]; Entry?: any[] },
): StoredBlogPost {
  const createdDate = new Date(entry.sys.createdAt)
  const dateStr = createdDate.toLocaleDateString('en-US', {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
  })

  const bodyText = extractPlainText(entry.fields.content)
  const excerpt =
    bodyText.slice(0, 200).trim() + (bodyText.length > 200 ? '…' : '')
  const wordCount = bodyText.split(/\s+/).length
  const readTime = `${Math.max(1, Math.ceil(wordCount / 200))} min read`

  // Resolve the blogImage linked asset
  const imageUrl = resolveAssetUrl(entry.fields.blogImage, includes)

  return {
    id: entry.sys.id,
    title: entry.fields.title || '',
    slug: entry.fields.slug || '',
    excerpt,
    body: bodyText,
    category: entry.fields.category || '',
    author: entry.fields.author || 'Belo Digital',
    image: imageUrl,
    readTime,
    date: dateStr,
    published: true,
  }
}

export function useBlogPosts() {
  const [allPosts, setAllPosts] = useState<StoredBlogPost[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)

  const fetchFromCMS = useCallback(async () => {
    setLoading(true)
    setError(false)
    try {
      const { items, includes } =
        await fetchContentfulEntriesWithIncludes<ContentfulBlogPostFields>(
          'blogPost',
        )
      const mapped = items.map((entry) =>
        mapContentfulBlogPost(entry, includes),
      )
      console.log(
        '[Blog] Fetched posts:',
        mapped.map((p) => ({ title: p.title, slug: p.slug, image: p.image })),
      )
      setAllPosts(mapped)
      saveBlogPosts(mapped)
    } catch (err) {
      console.error(
        'Failed to fetch blog posts from CMS, falling back to local:',
        err,
      )
      setError(true)
      setAllPosts(getStoredBlogPosts())
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchFromCMS()
  }, [fetchFromCMS])

  useEffect(() => {
    const handler = () => setAllPosts(getStoredBlogPosts())
    window.addEventListener(BLOG_POSTS_UPDATED_EVENT, handler)
    return () => window.removeEventListener(BLOG_POSTS_UPDATED_EVENT, handler)
  }, [])

  const posts = allPosts
    .filter((p) => p.published)
    .sort((a, b) => parseDate(b.date).getTime() - parseDate(a.date).getTime())

  return {
    posts,
    allPosts,
    loading,
    error,
    refetch: fetchFromCMS,
  }
}
